import PropTypes from 'prop-types'
import clsx from 'clsx'
import { format, parse } from 'date-fns'
import { ReactComponent as MapPin } from '../../../assets/icons/map-pin.svg'
import { useDestinationCountries } from '../../../services/destinationCountries'

import {
  Alert,
  Box,
  List,
  ListItem,
  TableContainer,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import StatusCodeChip from '../../../ui/components/chip/StatusCodeChip'
import { getWeekDay } from '../../../libs/date'
import {
  INCIDENT_CODE_INCORRECT_ADDRESS,
  RESOLVED_STATUS_CODES,
} from '../../../common/constants'
import useTranslations from '../../../i18n/useTranslations'
import useStyles from './styles'

const deriveChipColorClass = (isIncident, isUnscheduled) => {
  return isIncident ? 'primary' : isUnscheduled ? 'yellow' : 'info'
}

const deriveMUIAlertSeverity = (
  isCancelled,
  hasIncident,
  pickupUnscheduled,
) => {
  if (pickupUnscheduled) return 'warning'
  else if (isCancelled || hasIncident) return 'error'
  return 'info'
}

const TrackingStatus = ({
  incidentData,
  deliveryDate,
  cancellationDatetime,
  fullTrackingData,
  shippingInfo,
  isDelivered,
  isCancelled,
  isReturned,
}) => {
  const classes = useStyles()
  const t = useTranslations()
  const { data: countries } = useDestinationCountries()

  fullTrackingData = fullTrackingData.filter(
    x => !RESOLVED_STATUS_CODES.includes(x.shippingStatusCode),
  )

  const shouldShowAdditionalHelp =
    fullTrackingData[fullTrackingData.length - 1]?.incidentCode ===
    INCIDENT_CODE_INCORRECT_ADDRESS

  const hasIncident = Boolean(
    fullTrackingData[fullTrackingData.length - 1]?.incidentCode,
  )
  const countryName =
    countries?.find(country => country.code === shippingInfo.destinCountryCode)
      ?.label || shippingInfo.destinCountryCode

  const title = shippingInfo.pickupUnscheduled
    ? t('shipping-tracking:send:no-delivery-date:title')
    : hasIncident
    ? incidentData.description
    : isDelivered
    ? t('shipping-tracking:send:success:title')
    : isCancelled
    ? t('shipping-tracking:cancelled:title')
    : isReturned
    ? t('shipping-tracking:returned:title')
    : t('shipping-tracking:pickup:success:title')

  const subtitle = shippingInfo.pickupUnscheduled
    ? t('shipping-tracking:pickup:no-delivery-date:subtitle')
    : hasIncident
    ? t('shipping-tracking:pickup:error:subtitle')
    : isCancelled
    ? t('shipping-tracking:cancelled:subtitle', {
        datetime: cancellationDatetime,
      })
    : null

  const deliveryDateLiteral =
    deliveryDate === undefined
      ? t('shipping-tracking:pickup:not-defined')
      : `${getWeekDay(
          parse(deliveryDate, 'dd/MM/yyyy', new Date()),
        )} ${deliveryDate}`

  return (
    <Box>
      {deliveryDate < Date() && !shippingInfo.pickupUnscheduled ? (
        <div></div>
      ) : (
        <Box>
          <Typography
            variant="h2"
            className={clsx(classes.title, 'secondaryTitle', 'decorator')}
          >
            {t('shipping-tracking:current-status')}
          </Typography>
          <Alert
            severity={deriveMUIAlertSeverity(
              isCancelled,
              hasIncident,
              shippingInfo.pickupUnscheduled,
            )}
            className={
              shippingInfo.pickupUnscheduled
                ? classes.emptyDeliveryDate
                : classes.alert
            }
          >
            <Typography variant="h3medium" gutterBottom>
              {title}
            </Typography>

            {Boolean(subtitle) && (
              <Typography variant="body1" gutterBottom>
                {subtitle}
              </Typography>
            )}
            {!isCancelled ? (
              <Typography variant="body2">
                {shippingInfo.pickupUnscheduled ? null : deliveryDateLiteral}
              </Typography>
            ) : null}
            {shouldShowAdditionalHelp ? (
              <Box className={classes.additionalHelp}>
                <Typography variant="body1">
                  {t('shipping-tracking:check-pickup-address')}
                </Typography>
                <List className={classes.additionalHelpList}>
                  <ListItem>
                    {t(
                      'shipping-tracking:if-insert-address-is-not-correct-create-new-shipping',
                    )}
                  </ListItem>
                  <ListItem>
                    {t(
                      'shipping-tracking:if-address-is-correct-contact-customer-support',
                    )}
                  </ListItem>
                </List>
              </Box>
            ) : null}
          </Alert>
          <Box className={clsx(classes.alertBox)}>
            <div className={classes.iconContainer}>
              <MapPin />{' '}
            </div>
            <div>
              <Typography variant="h3" className={clsx(classes.body)}>
                {shippingInfo.recipientName}
              </Typography>
              <Typography variant="body1" className={clsx(classes.bodyGrey)}>
                {shippingInfo.recipientAddress}
              </Typography>
              <Typography variant="body1" className={clsx(classes.bodyGrey)}>
                {`${shippingInfo.destinPostalCode} ${shippingInfo.recipientAddress2}, ${countryName}`}
              </Typography>
              {shippingInfo.recipientPhone && (
                <Typography variant="body1" className={clsx(classes.bodyGrey)}>
                  {shippingInfo.recipientPhone}
                </Typography>
              )}
            </div>
          </Box>
        </Box>
      )}
      <Typography
        variant="h2"
        className={clsx(classes.title, 'secondaryTitle', 'decorator')}
      >
        {t('shipping-tracking:tracking')}.
        <span>{t('shipping-tracking:shipment-step-by-step')}</span>
      </Typography>

      <TableContainer component={Paper}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>{t('noun:date-and-time')}</TableCell>
              <TableCell align="left">{t('noun:shippingStatusCode')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shippingInfo?.pickupUnscheduled ? (
              <TableRow
                key={'unscheduled'}
                sx={
                  ({ '&:last-child td, &:last-child th': { border: 0 } },
                  {
                    '&:nth-child(even)': {
                      backgroundColor: 'neutral.lightest',
                    },
                  })
                }
              >
                <TableCell component="th" scope="row">
                  {format(
                    new Date(shippingInfo?.manifestDate),
                    'dd/MM/yyyy | HH:mm',
                  )}
                </TableCell>
                <TableCell align="left">
                  <StatusCodeChip
                    state={{
                      lastStatusDescription: t(
                        'tracking-line:pickup-unscheduled',
                      ),
                      severity: deriveChipColorClass(false, true),
                    }}
                    isCancelled={false}
                  />
                </TableCell>
              </TableRow>
            ) : null}
            {!fullTrackingData?.length ? (
              <TableRow
                key={'recorded'}
                sx={
                  ({ '&:last-child td, &:last-child th': { border: 0 } },
                  {
                    '&:nth-child(even)': {
                      backgroundColor: 'neutral.lightest',
                    },
                  })
                }
              >
                <TableCell component="th" scope="row">
                  {format(
                    new Date(shippingInfo?.manifestDate),
                    'dd/MM/yyyy | HH:mm',
                  )}
                </TableCell>
                <TableCell align="left">
                  <StatusCodeChip
                    state={{
                      lastStatusDescription: t('tracking:recorded'),
                      severity: deriveChipColorClass(),
                    }}
                    isCancelled={false}
                  />
                </TableCell>
              </TableRow>
            ) : null}
            {fullTrackingData.map((record, i) => {
              //ÑAPA CODIGO WEB1600
              const isIncident =
                fullTrackingData[i]?.incidentCode ||
                fullTrackingData[i]?.shippingStatusCode === 'WEB1600'
              return (
                <TableRow
                  key={record.name}
                  sx={
                    ({ '&:last-child td, &:last-child th': { border: 0 } },
                    {
                      '&:nth-child(even)': {
                        backgroundColor: 'neutral.lightest',
                      },
                    })
                  }
                >
                  <TableCell component="th" scope="row">
                    {record.dateTime}
                  </TableCell>
                  <TableCell align="left">
                    <StatusCodeChip
                      state={{
                        lastStatusDescription: record.eventName,
                        severity: deriveChipColorClass(isIncident),
                      }}
                      hasIncident={isIncident}
                      isCancelled={false}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

TrackingStatus.propTypes = {
  incidentData: PropTypes.object,
  deliveryDate: PropTypes.string,
  cancellationDatetime: PropTypes.instanceOf(Date),
  fullTrackingData: PropTypes.array,
  shippingInfo: PropTypes.object,
  isDelivered: PropTypes.bool,
  isCancelled: PropTypes.bool,
  isReturned: PropTypes.bool,
}

export default TrackingStatus
